/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { AttributeModel } from '@/api/types/products-types';
import Price from '@/components/AZCustomComponent/Price';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { RatingsReview } from '@/components/AZCustomComponent/RatingsReview';
import Title from '@/components/AZCustomComponent/Title';
import { TrapFocus } from '@/components/Dialog/Modal/TrapFocus';
import { Drawer } from '@/components/Drawer/Drawer';
import { Table } from '@/components/Table/Table';
import { TableBody } from '@/components/TableBody/TableBody';
import { TableCell } from '@/components/TableCell/TableCell';
import { TableRow } from '@/components/TableRow/TableRow';
import { routePaths } from '@/constants/routePaths';
import { selectOrderDetailsFromState } from '@/constants/selectors';
import type { AddToCartResponse } from '@/types/reduxStore/cartUpdate';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useLabels } from '@/hooks/useLabels';
import { useProductComparison } from '@/features/product/api/getProductComparison';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import type { AZBadgesFlagVOFromAPI, SkuPricingAndAvailabilityFromAPI } from '@/types/availability';
import type { ReduxState } from '@/types';
import { sessionStorage } from '@/utils/sessionStorage';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ComparisonTableBodyCell } from './components/ComparisonTableBodyCell/ComparisonTableBodyCell';
import { ComparisonTableHeaderCell } from './components/ComparisonTableHeaderCell/ComparisonTableHeaderCell';
import { ShopNowButton } from './components/ShopNowButton/ShopNowButton';
import sharedStyles from './sharedStyles.module.scss';
import styles from './style.module.scss';
import type { PDPComparisonAnalytics } from '@/types/analytics';

export type PDPComparisonTableProps = {
  parentBrandName: string;
  subBrandName: string | undefined;
  productId: string;
  skuId: string;
  partNumber: string;
  productName: string;
  price: number | undefined;
  averageRating: number | undefined;
  totalReviews: number | undefined;
  productAttributes: AttributeModel[] | undefined;
  productImage: string | null;
  hasPreferredVehicle: boolean;
  skuFitsVehicle: boolean;
  analyticsData: PDPComparisonAnalytics;
};

type ProductInformation = {
  name: string;
  image: string;
  skuPricingAndAvailability: SkuPricingAndAvailabilityFromAPI | undefined;
  aZBadgesFlagVO: AZBadgesFlagVOFromAPI | undefined;
  successData: AddToCartResponse & { quantity: string };
};

const LazyMiniCartFlyoutComp = dynamic(
  () =>
    import('@/features/miniCartFlyout/components/MiniCartFlyoutComp').then(
      (mod) => mod.MiniCartFlyoutComp
    ),
  {
    loading: () => <ProgressModal noScroll />,
  }
);

const labelMap = {
  customerRating: 'label_PDPComparison_customerRating',
  price: 'label_PDPComparison_price',
};

function PDPComparisonTableComp({
  skuId,
  partNumber,
  productName,
  price,
  averageRating,
  totalReviews,
  subBrandName,
  parentBrandName,
  productId,
  productAttributes,
  productImage,
  hasPreferredVehicle,
  skuFitsVehicle,
  analyticsData,
}: PDPComparisonTableProps) {
  const labels = useLabels(labelMap);
  const deviceType = useSelector<ReduxState>(({ appData }) => appData.deviceType);
  const isABot = deviceType === 'bot';
  const router = useRouter();
  const lazyMiniCartModalRef = useRef<HTMLDivElement>(null);
  const [showMiniCart, setShowMiniCart] = useState(false);
  const [selectedProductInformation, setSelectedProductInformation] =
    useState<ProductInformation | null>(null);
  const { data: headerData } = useHeaderData();

  const [queryEnabled, setQueryEnabled] = useState(() => isABot);

  useEffect(() => {
    const handleEnableQuery = () => {
      setQueryEnabled(true);
      window.removeEventListener('scroll', handleEnableQuery);
    };

    if (!isABot) {
      window.addEventListener('scroll', handleEnableQuery);
    }

    return () => {
      window.removeEventListener('scroll', handleEnableQuery);
    };
  }, [isABot]);

  const { data: productComparisonData } = useProductComparison({
    parentBrandName,
    subBrandName,
    productId,
    skuId,
    enabled: queryEnabled,
  });

  const attributes = useMemo(() => {
    const attributesObject: Record<string, string | null> = {};
    for (const attribute of productAttributes ?? []) {
      if (attribute.name) {
        attributesObject[attribute.name] = attribute.value ?? null;
      }
    }
    return attributesObject;
  }, [productAttributes]);

  const onPDPComparisonLinkClick = useCallback(() => {
    sessionStorage.setItem('productFindingMethod', 'SEO PDP Product Comparison');
  }, []);

  const { data: currentSkuDetails, refetch: refetchSKUDetails } = useProductSkuDetails({
    skuIds: [skuId],
  });

  const { data: comparisonSkuDetails } = useProductSkuDetails({
    skuIds: productComparisonData?.skus
      ? productComparisonData.skus.map((sku) => sku.skuId ?? '')
      : null,
    enabled: (productComparisonData?.skus?.length ?? 0) > 0,
  });

  const submitToCart = useCallback(async () => {
    await router.push(routePaths.cart);
  }, [router]);

  const handleOpenMinicart = useCallback((info: ProductInformation) => {
    setSelectedProductInformation({
      ...info,
    });
    setShowMiniCart(true);
  }, []);

  const handleCloseMiniCart = useCallback(() => {
    setShowMiniCart(false);
    setSelectedProductInformation(null);
  }, []);

  const orderDetailsFromState = useSelector(selectOrderDetailsFromState);

  return productComparisonData?.attributeNames?.length && productComparisonData.skus?.length ? (
    <div className={styles.comparisonContainer}>
      <Title
        firstText={productComparisonData.tableName}
        firstTextClass={styles.comparisonTableTitle}
      />
      <div className={styles.overflowShadowContainer}>
        <div className={styles.overflowShadow} />
        <div className={styles.tableWrapper}>
          <Table>
            <thead className={styles.comparisonTableHeader}>
              <TableRow>
                <TableCell component="th"></TableCell>

                <ComparisonTableHeaderCell
                  imageURL={productImage}
                  productName={productName}
                  productURL={null}
                  isCurrentSKU
                  onPDPComparisonLinkClick={onPDPComparisonLinkClick}
                />

                {productComparisonData.skus.map((sku) => (
                  <ComparisonTableHeaderCell
                    key={sku.skuId}
                    imageURL={sku.skuImgUrl}
                    productName={sku.skuName}
                    productURL={sku.skuSeoUrl}
                    onPDPComparisonLinkClick={onPDPComparisonLinkClick}
                  />
                ))}
              </TableRow>
            </thead>

            <TableBody className={styles.comparisonTableBody}>
              <TableRow className={styles.buttonsRow}>
                <TableCell></TableCell>

                <ShopNowButton
                  skuId={skuId}
                  skuDetails={currentSkuDetails?.[0]}
                  productId={productId}
                  partNumber={partNumber}
                  price={price}
                  showFitmentBadge={hasPreferredVehicle}
                  fitsVehicle={skuFitsVehicle}
                  analyticsData={analyticsData}
                  openMiniCart={(data) => {
                    handleOpenMinicart({
                      name: productName,
                      image: productImage ?? '',
                      skuPricingAndAvailability: currentSkuDetails?.[0].skuPricingAndAvailability,
                      aZBadgesFlagVO: currentSkuDetails?.[0].aZBadgesFlagVO ?? undefined,
                      successData: data,
                    });

                    void refetchSKUDetails();
                  }}
                />

                {productComparisonData.skus.map((sku, i) => {
                  return (
                    <ShopNowButton
                      key={sku.skuId}
                      skuDetails={comparisonSkuDetails?.[i]}
                      skuId={sku.skuId ?? ''}
                      partNumber={sku.partNumber}
                      productId={productId}
                      price={sku.price ?? undefined}
                      showFitmentBadge={hasPreferredVehicle}
                      fitsVehicle
                      analyticsData={analyticsData}
                      openMiniCart={(data) =>
                        handleOpenMinicart({
                          name: sku.skuName ?? '',
                          image: sku.skuImgUrl ?? '',
                          skuPricingAndAvailability:
                            comparisonSkuDetails?.[i].skuPricingAndAvailability,
                          aZBadgesFlagVO: comparisonSkuDetails?.[i].aZBadgesFlagVO ?? undefined,
                          successData: data,
                        })
                      }
                    />
                  );
                })}
              </TableRow>

              <TableRow className={styles.attributesRow}>
                <TableCell className={sharedStyles.comparisonCell} component="th" scope="row">
                  <p className={cx(sharedStyles.cellText, styles.attributeName)}>
                    {labels.customerRating}
                  </p>
                </TableCell>

                <ComparisonTableBodyCell
                  attributeValue={
                    shouldShowValue(totalReviews) ? (
                      <RatingsReview
                        averageRating={`${averageRating ?? 0}`}
                        totalReviews={`${totalReviews ?? 0}`}
                        containerClassName={styles.rating}
                        showStarFractions
                      />
                    ) : null
                  }
                />

                {productComparisonData.skus.map((sku) => (
                  <ComparisonTableBodyCell
                    key={sku.skuId}
                    attributeValue={
                      shouldShowValue(sku.totalReviewsCount) ? (
                        <RatingsReview
                          averageRating={`${sku.avgRating ?? 0}`}
                          totalReviews={`${sku.totalReviewsCount ?? 0}`}
                          containerClassName={styles.rating}
                          showStarFractions
                        />
                      ) : null
                    }
                  />
                ))}
              </TableRow>

              <TableRow className={styles.attributesRow}>
                <TableCell className={sharedStyles.comparisonCell} component="th" scope="row">
                  <p className={cx(sharedStyles.cellText, styles.attributeName)}>{labels.price}</p>
                </TableCell>

                <ComparisonTableBodyCell
                  attributeValue={
                    shouldShowValue(price) ? (
                      <Price
                        priceType="secondary"
                        dealPrice={price?.toFixed(2).split('.') ?? null}
                        wrapperClassName={styles.priceWrapper}
                        className={styles.price}
                      />
                    ) : null
                  }
                />

                {productComparisonData.skus.map((sku) => (
                  <ComparisonTableBodyCell
                    key={sku.skuId}
                    attributeValue={
                      shouldShowValue(sku.price) ? (
                        <Price
                          priceType="secondary"
                          dealPrice={sku.price?.toFixed(2).split('.') ?? null}
                          wrapperClassName={styles.priceWrapper}
                          className={styles.price}
                        />
                      ) : null
                    }
                  />
                ))}
              </TableRow>

              {productComparisonData.attributeNames.map((attributeName, attributeIndex) => (
                <TableRow key={attributeName} className={styles.attributesRow}>
                  <TableCell className={sharedStyles.comparisonCell} component="th" scope="row">
                    <p className={cx(sharedStyles.cellText, styles.attributeName)}>
                      {attributeName}
                    </p>
                  </TableCell>

                  <ComparisonTableBodyCell attributeValue={attributes[attributeName]} />

                  {productComparisonData.skus?.map((sku) => (
                    <ComparisonTableBodyCell
                      key={sku.skuId}
                      attributeValue={sku.attributeValues[attributeIndex]}
                    />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Drawer
        anchor="right"
        open={showMiniCart}
        onClose={handleCloseMiniCart}
        drawerRef={lazyMiniCartModalRef}
      >
        <TrapFocus open={showMiniCart}>
          <div className={styles.miniCartFlyoutDialog} role="dialog" aria-modal="true">
            {showMiniCart && !!selectedProductInformation && (
              <LazyMiniCartFlyoutComp
                handleClose={handleCloseMiniCart}
                isModalOpen={showMiniCart}
                productTitle={selectedProductInformation.name}
                image={selectedProductInformation.image}
                successData={selectedProductInformation.successData}
                productDetails={{
                  productPartType: selectedProductInformation.successData.originalPartTypeId,
                  originalPartTypeId: selectedProductInformation.successData.originalPartTypeId,
                  productAvailabilityInfo: {
                    aZBadgesFlagVO: selectedProductInformation.aZBadgesFlagVO,
                    dealsInfo: undefined,
                    skuPricingAndAvailability: selectedProductInformation.skuPricingAndAvailability,
                  },
                }}
                miniCartData={headerData?.miniCartMap}
                closeStoreModal={handleCloseMiniCart}
                cartUrl={submitToCart}
                pageName="pdp"
                orderDetailsFromState={orderDetailsFromState}
                parentRef={lazyMiniCartModalRef}
              />
            )}
          </div>
        </TrapFocus>
      </Drawer>
    </div>
  ) : null;
}

export const PDPComparisonTable = memo(PDPComparisonTableComp);

function shouldShowValue(value: number | null | undefined) {
  return !!value && value > 0;
}
